import React, { FC } from 'react';

import { EAssetType } from 'constants/enums';

import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import UploadImage from 'components/UploadImage/UploadImage';

import { useFormFieldsContext } from '../../../context';
import {
  FormFieldsBorderRow,
  FormFieldsGradientRow,
  FormFieldsIdentitySection,
  FormFieldsSection,
  FormFieldsUploadSection
} from '../components';

import {
  BG_BORDER_ROW_GRADIENT,
  BG_BORDER_ROW_WIDTH,
  BORDER_ROW_GRADIENT,
  BORDER_ROW_WIDTH,
  COLLECT_TEXT_FIELD,
  FREE_TILES_LAYOUT_OPTIONS,
  MAIN_UPLOAD_FIELDS,
  PLUS_IMAGE_UPLOAD,
  SUB_OFFER_BG_FIELDS
} from './BuyOneGetManyOfferDesignFormFields.consts';

const findFreeTilesLayoutOption = (currentValue: number | undefined) => {
  const stringValue = currentValue === undefined ? '2' : String(currentValue);
  return (
    FREE_TILES_LAYOUT_OPTIONS.find((option) => option.value === stringValue) ??
    FREE_TILES_LAYOUT_OPTIONS[1]
  );
};

export const BuyOneGetManyOfferDesignFormFields: FC =
  (): React.ReactElement => {
    const { values, setFieldValue, getFieldError, isFieldTouched } =
      useFormFieldsContext();

    const plusImageError = isFieldTouched('plusImage')
      ? getFieldError('plusImage')
      : undefined;

    const lockImageError = isFieldTouched('offersLockImage')
      ? getFieldError('offersLockImage')
      : undefined;

    return (
      <React.Fragment>
        <FormFieldsSection
          heading="1+Many"
          description="Customize the 1+Many offer to fit your store theme."
        >
          <FormFieldsIdentitySection
            extraFields={[COLLECT_TEXT_FIELD]}
            spacing="none"
          />
          <div className="mt-5">
            <InputSelectDropdown
              label="Free Tiles Layout"
              isRequired
              options={FREE_TILES_LAYOUT_OPTIONS}
              defaultSelected={findFreeTilesLayoutOption(
                values.freeTilesLayout
              )}
              onOptionChange={(value) =>
                setFieldValue('freeTilesLayout', Number(value))
              }
            />
          </div>
        </FormFieldsSection>

        <FormFieldsSection
          heading="Main Offer Design"
          showSeparator
          headingBottomMargin
        >
          <FormFieldsBorderRow
            gradientConfig={BG_BORDER_ROW_GRADIENT}
            widthConfig={BG_BORDER_ROW_WIDTH}
            spacing="sm"
          />

          <FormFieldsBorderRow
            gradientConfig={BORDER_ROW_GRADIENT}
            widthConfig={BORDER_ROW_WIDTH}
            spacing="sm"
          />

          <FormFieldsUploadSection fields={MAIN_UPLOAD_FIELDS} />

          <div className="mt-5">
            <UploadImage
              title={PLUS_IMAGE_UPLOAD.title}
              isRequired={PLUS_IMAGE_UPLOAD.isRequired}
              className="w-full !flex flex-row px-5"
              onUpload={(url) => setFieldValue('plusImage', url)}
              selectedImageUrl={values.plusImage || ''}
              forceType={PLUS_IMAGE_UPLOAD.forceType}
              TooltipContent={() => <span>{PLUS_IMAGE_UPLOAD.tooltip}</span>}
              hasError={!!plusImageError}
              errorMessage={plusImageError}
            />
          </div>
        </FormFieldsSection>

        <FormFieldsSection
          heading="Sub Offer Design"
          spacing="xl"
          headingBottomMargin
        >
          <FormFieldsGradientRow fields={SUB_OFFER_BG_FIELDS} spacing="sm" />

          <div className="mt-5">
            <UploadImage
              title="Offers background image"
              className="w-full !flex flex-row px-5"
              onUpload={(url) => setFieldValue('offersBackgroundImage', url)}
              selectedImageUrl={values.offersBackgroundImage || ''}
              forceType={EAssetType.BG_BUNDLE}
              TooltipContent={() => (
                <span>Upload an image for sub-offer background.</span>
              )}
            />
          </div>

          <div className="mt-5">
            <UploadImage
              title="Lock Image"
              isRequired
              className="w-full !flex flex-row px-5"
              onUpload={(url) => setFieldValue('offersLockImage', url)}
              selectedImageUrl={values.offersLockImage || ''}
              forceType={EAssetType.GENERAL}
              TooltipContent={() => (
                <span>
                  Upload the lock icon shown on locked sub-offers before they
                  are unlocked.
                </span>
              )}
              hasError={!!lockImageError}
              errorMessage={lockImageError}
            />
          </div>
        </FormFieldsSection>
      </React.Fragment>
    );
  };
